/**
 * AppReleasesPage — superadmin manager for mobile app releases / force-update.
 *
 * Sits at /superadmin/app-releases. Manages AppVersion rows via
 * GET/POST /superadmin/app-versions/ and PATCH /superadmin/app-versions/<id>/.
 *
 * Concepts (mirrors the backend contract):
 *   - One row per PUBLISHED release. The mobile app calls the public
 *     /app-version/check/ endpoint on startup; per platform it obeys the
 *     ACTIVE row with the highest build number.
 *   - Grace period ("duration", stored in seconds — picked in days here):
 *     during it users get a dismissible "update available" prompt; after it
 *     the app hard-blocks until they update. The countdown runs from the
 *     row's creation time, which is why publishing is always a NEW row —
 *     the backend rejects editing builds into an old row.
 *   - Retract (is_active=false): one click; the check endpoint instantly
 *     falls back to the previous active release.
 *   - A platform with build 0 is "not part of this release" (e.g. an
 *     Android-only hotfix leaves iOS on its previous row).
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  HiOutlineArrowLeft,
  HiOutlineCheckCircle,
  HiOutlineExclamationTriangle,
  HiOutlinePlus,
  HiOutlineDevicePhoneMobile,
  HiOutlineArrowUturnLeft,
  HiOutlineNoSymbol,
  HiOutlineBeaker,
} from 'react-icons/hi2'
import { appReleasesAPI } from '../../services/appReleases'

const NEVER_SECONDS = 3153600000        // ~100 years — "never force" sentinel
const NEVER_THRESHOLD = 315360000       // >= ~10 years reads as "never"
const DAY = 86400

const GRACE_PRESETS = [
  { value: '0',        label: 'Force immediately (no grace)' },
  { value: String(DAY),       label: '1 day' },
  { value: String(3 * DAY),   label: '3 days' },
  { value: String(7 * DAY),   label: '7 days' },
  { value: String(14 * DAY),  label: '14 days' },
  { value: String(30 * DAY),  label: '30 days' },
  { value: String(60 * DAY),  label: '60 days' },
  { value: String(90 * DAY),  label: '90 days' },
  { value: String(NEVER_SECONDS), label: 'Never force — recommend only' },
  { value: 'custom',   label: 'Custom (days)…' },
]

const EMPTY_FORM = {
  android_version: '', android_build: '', android_url: '',
  ios_version: '', ios_build: '', ios_url: '',
  gracePreset: String(7 * DAY), customDays: '',
  min_supported_build_android: '', min_supported_build_ios: '',
}

function humanGrace(seconds) {
  if (seconds === 0) return 'Immediate'
  if (seconds >= NEVER_THRESHOLD) return 'Never (recommend only)'
  if (seconds < DAY) return `${Math.max(1, Math.round(seconds / 3600))} h`
  const days = Math.round(seconds / DAY)
  return `${days} day${days === 1 ? '' : 's'}`
}

function FlashMessage({ tone, children, onDismiss }) {
  const tones = {
    success: 'bg-green-50 border-green-200 text-green-800',
    error:   'bg-red-50 border-red-200 text-red-800',
    info:    'bg-blue-50 border-blue-200 text-blue-800',
  }
  const Icon = tone === 'success' ? HiOutlineCheckCircle : HiOutlineExclamationTriangle
  return (
    <div className={`flex items-start gap-3 px-4 py-3 rounded-lg border ${tones[tone] || tones.info} mb-4`}>
      <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" />
      <div className="flex-1 text-sm">{children}</div>
      {onDismiss && (
        <button onClick={onDismiss} className="text-xs opacity-70 hover:opacity-100">Dismiss</button>
      )}
    </div>
  )
}

const inputCls =
  'w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#29AECB]/40 focus:border-[#29AECB]'
const labelCls = 'block text-xs font-medium text-gray-600 mb-1.5'

function PlatformFields({ title, values, onChange, prefix }) {
  const set = (k, v) => onChange({ ...values, [k]: v })
  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Version <span className="text-gray-400">(e.g. 1.1.0)</span></label>
          <input
            type="text" maxLength={20}
            value={values[`${prefix}_version`]}
            onChange={(e) => set(`${prefix}_version`, e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls}>Build <span className="text-gray-400">(the +N — must increase)</span></label>
          <input
            type="number" min="0"
            value={values[`${prefix}_build`]}
            onChange={(e) => set(`${prefix}_build`, e.target.value)}
            placeholder="0 = not in this release"
            className={inputCls}
          />
        </div>
      </div>
      <div>
        <label className={labelCls}>Store URL</label>
        <input
          type="url"
          value={values[`${prefix}_url`]}
          onChange={(e) => set(`${prefix}_url`, e.target.value)}
          placeholder="https://…"
          className={inputCls}
        />
      </div>
    </div>
  )
}

export default function AppReleasesPage() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [releases, setReleases] = useState([])
  const [flash, setFlash] = useState(null)

  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState(EMPTY_FORM)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [rowBusy, setRowBusy] = useState(null)

  // Inline "what would a user see?" self-check
  const [checkPlatform, setCheckPlatform] = useState('android')
  const [checkBuild, setCheckBuild] = useState('1')
  const [checkResult, setCheckResult] = useState(null)
  const [checking, setChecking] = useState(false)

  const load = useCallback(async () => {
    try {
      const res = await appReleasesAPI.list()
      if (res.success) {
        setReleases(res.data?.data?.releases || [])
        setLoadError('')
      } else {
        setLoadError(res.error?.message || 'Failed to load releases')
      }
    } catch {
      setLoadError('Network error — please refresh and try again')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  // Which row is "current" per platform = active row with the highest build
  // (ties → newest). Mirrors the backend's pick so the chips are truthful.
  const currentIds = useMemo(() => {
    const pick = (platform) => {
      const candidates = releases.filter(
        (r) => r.is_active && (r[`${platform}_build`] || 0) > 0
      )
      if (!candidates.length) return null
      return candidates.reduce((best, r) => {
        const b = r[`${platform}_build`], bb = best[`${platform}_build`]
        if (b > bb) return r
        if (b === bb && new Date(r.created_at) > new Date(best.created_at)) return r
        return best
      }).id
    }
    return { android: pick('android'), ios: pick('ios') }
  }, [releases])

  const openForm = () => {
    // Prefill store URLs (and version labels as a starting point) from the
    // newest release so a routine publish is mostly "type the new numbers".
    const latest = releases[0]
    setForm({
      ...EMPTY_FORM,
      android_url: latest?.android_url || '',
      ios_url: latest?.ios_url || '',
      android_version: latest?.android_version || '',
      ios_version: latest?.ios_version || '',
    })
    setShowAdvanced(false)
    setShowForm(true)
  }

  const durationSeconds = () => {
    if (form.gracePreset === 'custom') {
      const days = parseFloat(form.customDays)
      if (Number.isNaN(days) || days < 0) return null
      return Math.round(days * DAY)
    }
    return parseInt(form.gracePreset, 10)
  }

  const handlePublish = async () => {
    setFlash(null)
    const duration = durationSeconds()
    if (duration === null) {
      setFlash({ tone: 'error', message: 'Enter a valid number of days for the custom grace period.' })
      return
    }
    const androidBuild = parseInt(form.android_build, 10) || 0
    const iosBuild = parseInt(form.ios_build, 10) || 0
    if (!androidBuild && !iosBuild) {
      setFlash({ tone: 'error', message: 'Set a build number for at least one platform.' })
      return
    }
    setPublishing(true)
    try {
      const res = await appReleasesAPI.publish({
        android_version: form.android_version.trim(),
        android_build: androidBuild,
        android_url: form.android_url.trim(),
        ios_version: form.ios_version.trim(),
        ios_build: iosBuild,
        ios_url: form.ios_url.trim(),
        duration,
        min_supported_build_android:
          form.min_supported_build_android === '' ? null : parseInt(form.min_supported_build_android, 10),
        min_supported_build_ios:
          form.min_supported_build_ios === '' ? null : parseInt(form.min_supported_build_ios, 10),
      })
      if (res.success) {
        setShowForm(false)
        setFlash({ tone: 'success', message: 'Release published — the app picks it up on next startup check.' })
        await load()
      } else {
        setFlash({ tone: 'error', message: res.error?.message || 'Failed to publish release' })
      }
    } catch {
      setFlash({ tone: 'error', message: 'Network error — please try again' })
    } finally {
      setPublishing(false)
    }
  }

  const setActive = async (row, active) => {
    const verb = active ? 'Reactivate' : 'Retract'
    if (!active && !window.confirm(
      'Retract this release? The app immediately falls back to the previous active release for affected platforms.'
    )) return
    setRowBusy(row.id)
    setFlash(null)
    try {
      const res = await appReleasesAPI.update(row.id, { is_active: active })
      if (res.success) {
        setFlash({ tone: 'success', message: `${verb}ed.` })
        await load()
      } else {
        setFlash({ tone: 'error', message: res.error?.message || `Failed to ${verb.toLowerCase()}` })
      }
    } catch {
      setFlash({ tone: 'error', message: 'Network error — please try again' })
    } finally {
      setRowBusy(null)
    }
  }

  const runCheck = async () => {
    setChecking(true)
    setCheckResult(null)
    try {
      const res = await appReleasesAPI.check(checkPlatform, parseInt(checkBuild, 10) || 0)
      const d = res.data?.data
      if (res.success && d) {
        setCheckResult(d)
      } else {
        setCheckResult({ error: res.error?.message || 'Check failed' })
      }
    } catch {
      setCheckResult({ error: 'Network error' })
    } finally {
      setChecking(false)
    }
  }

  const graceCell = (row) => {
    const label = humanGrace(row.duration)
    if (!row.is_active || row.duration >= NEVER_THRESHOLD) return label
    if (row.duration === 0) return <span className="text-red-700 font-medium">Immediate</span>
    if (row.forced_at && new Date(row.forced_at) <= new Date()) {
      return <span className="text-red-700 font-medium">{label} — forcing now</span>
    }
    return (
      <>
        {label}
        {row.forced_at && (
          <span className="block text-[11px] text-gray-400">
            forces {new Date(row.forced_at).toLocaleDateString()}
          </span>
        )}
      </>
    )
  }

  const platformCell = (row, prefix) => {
    const build = row[`${prefix}_build`] || 0
    if (!build) return <span className="text-gray-300">—</span>
    const platformKey = prefix === 'android' ? 'android' : 'ios'
    const isCurrent = currentIds[platformKey] === row.id
    return (
      <div>
        <span className="font-medium text-gray-900">{row[`${prefix}_version`] || '?'}</span>
        <span className="text-gray-500"> +{build}</span>
        {isCurrent && (
          <span className="ml-1.5 inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded bg-[#29AECB]/10 text-[#1d7e94] border border-[#29AECB]/30">
            Current
          </span>
        )}
        {row[`min_supported_build_${platformKey}`] != null && (
          <span className="block text-[11px] text-amber-700">
            hard floor: build {row[`min_supported_build_${platformKey}`]}
          </span>
        )}
      </div>
    )
  }

  if (loading) {
    return <div className="p-6 text-sm text-gray-500">Loading app releases…</div>
  }

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/superadmin')}
            className="p-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100"
            aria-label="Back to dashboard"
          >
            <HiOutlineArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <HiOutlineDevicePhoneMobile className="w-5 h-5 text-[#29AECB]" />
              App Releases
            </h1>
            <p className="text-xs text-gray-500">
              Mobile update prompts & force-update. The app checks on every startup;
              soft prompt during the grace period, hard block after it.
            </p>
          </div>
        </div>

        {!showForm && (
          <button
            onClick={openForm}
            className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-[#29AECB] hover:bg-[#1d9bb5] rounded-lg"
          >
            <HiOutlinePlus className="w-4 h-4" />
            Publish release
          </button>
        )}
      </div>

      {flash && (
        <FlashMessage tone={flash.tone} onDismiss={() => setFlash(null)}>
          {flash.message}
        </FlashMessage>
      )}
      {loadError && <FlashMessage tone="error">{loadError}</FlashMessage>}

      {/* Publish form */}
      {showForm && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-5 space-y-5">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-gray-900">Publish a new release</h2>
            <p className="text-xs text-gray-500">
              Builds must match the app's <code className="text-[11px]">pubspec</code> +N and increase every release.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <PlatformFields title="Android" prefix="android" values={form} onChange={setForm} />
            <PlatformFields title="iOS" prefix="ios" values={form} onChange={setForm} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
            <div>
              <label className={labelCls}>
                Grace period <span className="text-gray-400">(soft prompt before the hard block)</span>
              </label>
              <select
                value={form.gracePreset}
                onChange={(e) => setForm({ ...form, gracePreset: e.target.value })}
                className={inputCls}
              >
                {GRACE_PRESETS.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>
            {form.gracePreset === 'custom' && (
              <div>
                <label className={labelCls}>Custom grace (days)</label>
                <input
                  type="number" min="0" step="0.5"
                  value={form.customDays}
                  onChange={(e) => setForm({ ...form, customDays: e.target.value })}
                  className={inputCls}
                />
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            className="text-xs font-medium text-[#29AECB] hover:underline"
          >
            {showAdvanced ? 'Hide advanced' : 'Advanced: instant hard floor…'}
          </button>
          {showAdvanced && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>
                  Min supported Android build <span className="text-gray-400">(blocks older builds instantly, ignores grace)</span>
                </label>
                <input
                  type="number" min="0"
                  value={form.min_supported_build_android}
                  onChange={(e) => setForm({ ...form, min_supported_build_android: e.target.value })}
                  placeholder="empty = none"
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>
                  Min supported iOS build <span className="text-gray-400">(same, for iOS)</span>
                </label>
                <input
                  type="number" min="0"
                  value={form.min_supported_build_ios}
                  onChange={(e) => setForm({ ...form, min_supported_build_ios: e.target.value })}
                  placeholder="empty = none"
                  className={inputCls}
                />
              </div>
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-1">
            <button
              onClick={() => setShowForm(false)}
              className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handlePublish}
              disabled={publishing}
              className="px-5 py-2 text-sm font-semibold text-white bg-[#29AECB] hover:bg-[#1d9bb5] rounded-lg disabled:opacity-50"
            >
              {publishing ? 'Publishing…' : 'Publish release'}
            </button>
          </div>
        </div>
      )}

      {/* Releases list */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b border-gray-200 bg-gray-50">
                <th className="px-4 py-2.5 font-medium">Published</th>
                <th className="px-4 py-2.5 font-medium">Android</th>
                <th className="px-4 py-2.5 font-medium">iOS</th>
                <th className="px-4 py-2.5 font-medium">Grace</th>
                <th className="px-4 py-2.5 font-medium">Status</th>
                <th className="px-4 py-2.5 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {releases.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-400 text-sm">
                    No releases yet — publish the first one.
                  </td>
                </tr>
              )}
              {releases.map((row) => (
                <tr key={row.id} className={`border-b border-gray-100 last:border-0 ${row.is_active ? '' : 'opacity-60'}`}>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                    {row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-4 py-3">{platformCell(row, 'android')}</td>
                  <td className="px-4 py-3">{platformCell(row, 'ios')}</td>
                  <td className="px-4 py-3 text-gray-600">{graceCell(row)}</td>
                  <td className="px-4 py-3">
                    {row.is_active ? (
                      <span className="inline-flex px-2 py-0.5 text-[11px] font-medium rounded-full bg-green-50 text-green-700 border border-green-200">
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex px-2 py-0.5 text-[11px] font-medium rounded-full bg-gray-100 text-gray-500 border border-gray-200">
                        Retracted
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {row.is_active ? (
                      <button
                        onClick={() => setActive(row, false)}
                        disabled={rowBusy === row.id}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-700 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50"
                      >
                        <HiOutlineNoSymbol className="w-3.5 h-3.5" />
                        {rowBusy === row.id ? 'Working…' : 'Retract'}
                      </button>
                    ) : (
                      <button
                        onClick={() => setActive(row, true)}
                        disabled={rowBusy === row.id}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                      >
                        <HiOutlineArrowUturnLeft className="w-3.5 h-3.5" />
                        {rowBusy === row.id ? 'Working…' : 'Reactivate'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Self-check */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mt-5">
        <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-3">
          <HiOutlineBeaker className="w-4 h-4 text-[#29AECB]" />
          What would a user see?
        </h2>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className={labelCls}>Platform</label>
            <select value={checkPlatform} onChange={(e) => setCheckPlatform(e.target.value)} className={inputCls}>
              <option value="android">Android</option>
              <option value="ios">iOS</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>User's build</label>
            <input
              type="number" min="0"
              value={checkBuild}
              onChange={(e) => setCheckBuild(e.target.value)}
              className={inputCls}
            />
          </div>
          <button
            onClick={runCheck}
            disabled={checking}
            className="px-4 py-2 text-sm font-medium text-[#29AECB] border border-[#29AECB]/40 rounded-lg hover:bg-[#29AECB]/5 disabled:opacity-50"
          >
            {checking ? 'Checking…' : 'Run check'}
          </button>
          {checkResult && (
            <div className="text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
              {checkResult.error ? (
                <span className="text-red-700">{checkResult.error}</span>
              ) : checkResult.force_update ? (
                <span className="text-red-700 font-medium">
                  HARD BLOCK — must update to {checkResult.latest_version} (build {checkResult.latest_build})
                </span>
              ) : checkResult.update_available ? (
                <span className="text-amber-700 font-medium">
                  Soft prompt — update to {checkResult.latest_version} available;
                  forced in ~{Math.ceil((checkResult.seconds_until_forced || 0) / DAY)} day(s)
                </span>
              ) : (
                <span className="text-green-700 font-medium">Up to date — nothing shown</span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
